(function() {
  if (typeof customElements === 'undefined') return;
  
  const ATTR_MONEY_TEMPLATE = "money-template";
  const ATTR_MAIN_VARIANT_PRICE = "main-variant-price";
  const ATTR_SHOW_TOTAL = 'show-total';

  class ProductBuyWith extends HTMLElement {
    constructor() {
      super();
      this.checkedItems = [];
      this.productFormId = null;
      this._isAddingComplementary = false;
      this._pendingComplementaryItems = [];
    }

    connectedCallback() {
      this.addEventListener('change', this._onChange.bind(this));
      this._getCurrentItems();
      this._findProductForm();
      this._attachFormListener();
    }

    _findProductForm() {
      // Find the product form by checking checkboxes form attribute
      const checkboxes = this.querySelectorAll('input[data-element="complementary-item"]');
      if (checkboxes.length > 0) {
        const formId = checkboxes[0].getAttribute('form');
        if (formId) {
          this.productFormId = formId;
        }
      }
    }

    _attachFormListener() {
      if (!this.productFormId) {
        console.warn('ProductBuyWith: productFormId not found');
        return;
      }
      
      const form = document.getElementById(this.productFormId);
      if (!form) {
        console.warn('ProductBuyWith: form not found with id', this.productFormId);
        return;
      }

      // Find the product-form-component
      const productFormComponent = form.closest('product-form-component');
      if (!productFormComponent) {
        console.warn('ProductBuyWith: product-form-component not found');
        return;
      }

      // Store reference to product form component
      this.productFormComponent = productFormComponent;
      this.productFormComponentId = productFormComponent.id || productFormComponent.dataset.sectionId;
      this.sectionId = productFormComponent.dataset.sectionId;
      this.productId = productFormComponent.dataset.productId;
      
      console.log('ProductBuyWith: Attached listener', {
        formId: this.productFormId,
        componentId: this.productFormComponentId,
        sectionId: this.sectionId,
        productId: this.productId
      });

      // Intercept form submit to capture checked items BEFORE submission
      const handleFormSubmit = (event) => {
        // Capture checked items before form is submitted
        this._getCurrentItems();
        this._pendingComplementaryItems = [...this.checkedItems];
        
        console.log('ProductBuyWith: Form submit intercepted', {
          pendingItemsCount: this._pendingComplementaryItems.length
        });
        
        // If there are complementary items, add them immediately in parallel with main product
        if (this._pendingComplementaryItems.length > 0) {
          // Don't wait for cart:update event, add immediately
          const itemsToAdd = [...this._pendingComplementaryItems];
          this._pendingComplementaryItems = [];
          
          // Add complementary products in parallel (don't await, let it run in background)
          this._addComplementaryProductsWithItems(itemsToAdd).catch(error => {
            console.error('ProductBuyWith: Error adding complementary products on submit:', error);
          });
        }
      };

      form.addEventListener('submit', handleFormSubmit, { capture: true });
      this._formSubmitHandler = handleFormSubmit;

      // Listen for cart update events that come from this product form (backup in case submit handler didn't work)
      const handleCartUpdate = (event) => {
        // Skip if we're already adding complementary products
        if (this._isAddingComplementary) {
          console.log('ProductBuyWith: Skipping, already adding complementary products');
          return;
        }
        
        // If we already processed items on submit, don't process again
        if (this._pendingComplementaryItems.length === 0) {
          return;
        }
        
        // Check if this event came from our product form component
        const sourceId = event.detail?.sourceId;
        const source = event.detail?.data?.source;
        const didError = event.detail?.data?.didError;
        const eventProductId = event.detail?.data?.productId;
        
        console.log('ProductBuyWith: cart:update event (backup handler)', {
          sourceId,
          ourComponentId: this.productFormComponentId,
          ourSectionId: this.sectionId,
          source,
          didError,
          eventProductId,
          ourProductId: this.productId,
          pendingItemsCount: this._pendingComplementaryItems.length
        });
        
        // Match by checking if:
        // 1. Source is product-form-component
        // 2. No error occurred
        // 3. Either sourceId matches our section/component ID, OR productId matches
        const isFromOurForm = source === 'product-form-component' && 
                              !didError &&
                              (sourceId === this.productFormComponentId || 
                               sourceId === this.sectionId ||
                               eventProductId === this.productId ||
                               (this.sectionId && this.productFormId.includes(this.sectionId)));
        
        if (isFromOurForm && this._pendingComplementaryItems.length > 0) {
          console.log('ProductBuyWith: Adding complementary products (backup)', this._pendingComplementaryItems.length);
          // Use the captured items instead of current checked items
          const itemsToAdd = this._pendingComplementaryItems;
          this._pendingComplementaryItems = [];
          
          // Add complementary products immediately
          this._addComplementaryProductsWithItems(itemsToAdd).catch(error => {
            console.error('ProductBuyWith: Error adding complementary products on cart update:', error);
          });
        } else {
          // Clear pending items if not from our form
          if (!isFromOurForm) {
            this._pendingComplementaryItems = [];
          }
        }
      };

      document.addEventListener('cart:update', handleCartUpdate);
      this._cartUpdateHandler = handleCartUpdate;
    }

    disconnectedCallback() {
      if (this._cartUpdateHandler) {
        document.removeEventListener('cart:update', this._cartUpdateHandler);
      }
      if (this._formSubmitHandler && this.productFormId) {
        const form = document.getElementById(this.productFormId);
        if (form) {
          form.removeEventListener('submit', this._formSubmitHandler, { capture: true });
        }
      }
    }

    async _addComplementaryProducts() {
      // Get fresh checked items
      this._getCurrentItems();
      this._addComplementaryProductsWithItems(this.checkedItems);
    }

    async _addComplementaryProductsWithItems(checkedItems) {
      if (!checkedItems || checkedItems.length === 0) {
        console.log('ProductBuyWith: No items to add');
        return;
      }

      console.log('ProductBuyWith: Starting to add', checkedItems.length, 'complementary products');

      // Set flag to prevent unchecking during addition
      this._isAddingComplementary = true;

      // Add all complementary products to cart in parallel for faster execution
      const addPromises = checkedItems.map(async (checkbox) => {
        const variantId = checkbox.value;
        if (!variantId) {
          console.warn('ProductBuyWith: Checkbox has no variant ID');
          return null;
        }

        console.log('ProductBuyWith: Adding variant', variantId);

        try {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('ProductBuyWith: Failed to add complementary product:', variantId, errorData);
            return { success: false, variantId, error: errorData };
          } else {
            const result = await response.json();
            console.log('ProductBuyWith: Successfully added variant', variantId, result);
            return { success: true, variantId, result };
          }
        } catch (error) {
          console.error('ProductBuyWith: Error adding complementary product:', error);
          return { success: false, variantId, error };
        }
      });

      // Wait for all products to be added in parallel
      await Promise.all(addPromises);

      // Fetch updated cart and dispatch cart update event
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        console.log('ProductBuyWith: Cart updated, items:', cart.items.length);
        
        // Dispatch cart update event with cart data
        const cartUpdateEvent = new CustomEvent('cart:update', { 
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.productFormComponentId,
            data: {
              source: 'product-buy-with'
            }
          }
        });
        document.dispatchEvent(cartUpdateEvent);
      } catch (error) {
        console.error('ProductBuyWith: Error fetching cart after adding complementary products:', error);
        // Still dispatch event even if fetch fails
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      }
      
      // Reset flag and uncheck items after adding
      this._isAddingComplementary = false;
      this._uncheck();
      
      console.log('ProductBuyWith: Finished adding complementary products');
    }

    _onChange() {      
      this._getCurrentItems();
      if (this.showTotal) {
        this._updateSubtotal();
      }
      this._publishChange();
    } 

    _getCurrentItems() {
      const checkboxes = this.querySelectorAll('input[data-element="complementary-item"]');
      this.checkedItems = Array.from(checkboxes).filter(item => item.checked);
    }

    _uncheck() {
      this.checkedItems.forEach(checkbox => {
        checkbox.checked = false;
      });
      const subtotal = this.querySelector('[data-element="subtotal"]');
      if (subtotal) {
        subtotal.hidden = true;
      }
      this._getCurrentItems();
      this._publishChange();
    }

    _publishChange() {
      const event = new CustomEvent('buyWith:change', { 
        detail: { 
          selected: this.checkedItems.length
        }
      });
      document.dispatchEvent(event);
    }

    _updateSubtotal() {
      this._getSubtotalWithCurrency();
      const subtotalPrice = this.querySelector('[data-element="subtotal-price"]');
      if (subtotalPrice) {
        subtotalPrice.textContent = this.subtotalWithCurrency;
      }
      const subtotal = this.querySelector('[data-element="subtotal"]');
      if (subtotal) {
        subtotal.hidden = !this.checkedItems.length;
      }
    }

    _getSubtotalWithCurrency() {
      this.subtotalWithCurrency = this.moneyTemplate.replace('{amount}', this.subtotalValue);
    }

    get subtotalValue() {
      const totalInCents = this.checkedItems
        .map(item => +item.dataset.priceValue)
        .reduce((acc, price) => acc += price, +this.mainVariantPrice);

      const total = totalInCents / 100;
      return total.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
    }

    get mainVariantPrice() {
      return this.getAttribute(ATTR_MAIN_VARIANT_PRICE) || '0';
    }

    get moneyTemplate() {
      return this.getAttribute(ATTR_MONEY_TEMPLATE) || 'R$ {amount}';
    }

    get showTotal() {
      return this.hasAttribute(ATTR_SHOW_TOTAL);
    }
  }

  customElements.define('product-buy-with', ProductBuyWith);

  // Listen for cart updates to uncheck items (only if not adding complementary products)
  document.addEventListener('cart:update', function(event) {
    // Skip if this event came from product-buy-with (we handle it internally)
    if (event.detail?.data?.source === 'product-buy-with') {
      return;
    }
    
    const buyWithElements = document.querySelectorAll('product-buy-with');
    buyWithElements.forEach(element => {
      // Only uncheck if not currently adding complementary products
      if (element._uncheck && !element._isAddingComplementary) {
        element._uncheck();
      }
    });
  });
})();

